import React from 'react';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import draftEntryUiHelper from 'helpers/draft-entry-ui-helper';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from 'util/api';
import enums from 'util/enums';
import utils from 'util/utils';
import idGenerator from 'util/idGenerator';
import logger from 'util/logger';
import segmentHelper from 'helpers/segment-helper';

const DuplicateRecord = ({
  showDuplicateModal,
  setShowDuplicateModal,
  selectedDuplicateRecords,
  setSelectedDuplicateRecords,
  loadMasterSiners
}) => {
  const [selectedRecordsData, setSelectedRecordsData] = useState([]);
  const [parentSigner, setParentSigner] = useState(null);
  const [selectedIdentifications, setSelectedIdentifications] = useState([]);
  const [isCompleteMargeModel, setIsCompleteMargeModel] = useState(false);
  const [isFinalConfirmationModel, setIsFinalConfirmationModel] =
    useState(false);
  const [isconfirm, setIsConfirm] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const toastSuccess = message => {
    toast.success(message, {
      theme: 'colored'
    });
  };

  const fetchSignerDetails = async () => {
    setIsLoading(true);
    const fetchSignerDetails = await api.masterSigner
      .get(selectedDuplicateRecords?.GUID, true)
      .then(res => {
        setIsLoading(false);
        return res;
      })
      .catch(() => {
        return null;
      });
    setSelectedRecordsData([fetchSignerDetails]);
  };

  useEffect(() => {
    if (selectedDuplicateRecords) {
      fetchSignerDetails();
    }
  }, [selectedDuplicateRecords]);

  const handleRadioChange = GUID => {
    setParentSigner(GUID);
  };

  const findParentChildSigner = (guid, data) => {
    // First, try to find in top-level
    const topLevelMatch = data.find(signer => signer.GUID === guid);
    if (topLevelMatch) return topLevelMatch;

    // Then, try to find in PotentialDuplicateSigner arrays
    for (const signer of data) {
      const potentialMatch = signer.PotentialDuplicateSigner?.find(
        dup => dup.GUID === guid
      );
      if (potentialMatch) return potentialMatch;
    }

    return null;
  };

  const handleSubmit = () => {
    setIsSubmitLoading(true);

    const signerList = [
      selectedRecordsData[0],
      ...(selectedRecordsData[0]?.PotentialDuplicateSigner || [])
    ];
    const IDs = signerList.map(s => s.GUID);
    // Filter out the parentSigner to get the child records
    const childsOfParentSigner = IDs?.filter(master => master !== parentSigner);
    const children = signerList
      .filter(s => s.GUID !== parentSigner)
      .map(s => `**${s.FirstName} ${s.LastName}$$${s.GUID}**`)
      .join(', ');

    const parentBlobs = {
      Name: 'AuditLog',
      Description: `AuditLog`,
      ContentType: enums.CONTENT_TYPES.APPLICATION_JSON,
      RefObjGUID: parentSigner,
      Data: utils.encodeObjectForBlobData({
        audit_log_item_id: idGenerator.getAuditLogItemId(),
        description: `The records for ${children} were merged with this client.`,
        timestamp: moment().utc().format()
      })
    };

    const childBlobs = childsOfParentSigner.map(childGUID => {
      const child = signerList.find(s => s.GUID === childGUID);
      const parent = findParentChildSigner(parentSigner, selectedRecordsData);
      const childAuditLog = {
        audit_log_item_id: idGenerator.getAuditLogItemId(),
        description: `The record for **${child?.FirstName} ${child?.LastName}$$${child?.GUID}** were merged with **${parent?.FirstName} ${parent?.LastName}##${parent?.GUID}**.`,
        timestamp: moment().utc().format()
      };

      return {
        Name: 'AuditLog',
        Description: 'AuditLog',
        ContentType: enums.CONTENT_TYPES.APPLICATION_JSON,
        RefObjGUID: childGUID,
        Data: utils.encodeObjectForBlobData([childAuditLog])
      };
    });

    let data = {
      ParentMasterSignerGUID: parentSigner,
      ChildMasterSignerGUID: childsOfParentSigner,
      SelectedIdentificationGUID: selectedIdentifications,
      Blobs: [parentBlobs, ...childBlobs]
    };

    parentSigner &&
      api.mergeSigner
        .mergeSignerData(data)
        .then(() => {
          setIsSubmitLoading(false);
          loadMasterSiners();
          setIsConfirm(!isconfirm);
          setIsCompleteMargeModel(false);
          setIsFinalConfirmationModel(false);
          setShowDuplicateModal(!showDuplicateModal);
          setParentSigner(null);
          setSelectedRecordsData([]);
          setSelectedDuplicateRecords([]);
          setSelectedIdentifications([]);
          toastSuccess(`Duplicate clients merged successfully.`);
          segmentHelper.track(
            segmentHelper.events.MASTER_CLIENT_DUPLICATE_RECORDS_MERGED
          );
        })
        .catch(error => {
          logger.error('Error merging duplicate master client records', error);
        });
  };

  const handleCompleteConfirm = () => {
    setIsCompleteMargeModel(true);
  };

  const handleIdentification = (e, identification) => {
    const { GUID, IdType } = identification;

    setSelectedIdentifications(prev => {
      const updated = new Set(prev);

      if (updated.has(GUID)) {
        // Uncheck if already selected
        updated.delete(GUID);
      } else {
        // Remove same IdType from selectedRecordsData
        selectedRecordsData.forEach(signer => {
          (signer.Identifications || []).forEach(id => {
            if (id.IdType === IdType && updated.has(id.GUID)) {
              updated.delete(id.GUID);
            }
          });
        });

        // Also check potential duplicates
        const duplicateSigners = selectedRecordsData.flatMap(
          rec => rec.PotentialDuplicateSigner || []
        );

        duplicateSigners.forEach(signer => {
          (signer.Identifications || []).forEach(id => {
            if (id.IdType === IdType && updated.has(id.GUID)) {
              updated.delete(id.GUID);
            }
          });
        });

        updated.add(GUID);
      }

      return Array.from(updated);
    });
  };

  const getIdentificationViewCardForMasterSigner = (identity, record) => {
    return identity ? (
      <Col
        lg={4}
        className="mb-3"
        key={_.uniqueId('identification-view') + identity.GUID}
      >
        <Card className="h-md-100">
          <Card.Header className="pb-0">
            <Flex
              justifyContent="between"
              alignContent="center"
              alignItems="center"
              className="pb-2"
            >
              <Flex
                justifyContent="center"
                alignContent="center"
                alignItems="center"
              >
                <Form.Check
                  type="checkbox"
                  className="me-3"
                  onClick={e => handleIdentification(e, identity, record)}
                  checked={selectedIdentifications.includes(identity.GUID)} // Check if GUID is in selectedIdentifications state
                  key={identity.GUID} // Assuming identification has a GUID property
                />
                {/* <FontAwesomeIcon icon={faIdBadge} size="lg" className="me-3" /> */}
                <h5 className="mb-0 mb-1">
                  Identification
                  <SoftBadge
                    pill
                    bg={
                      identity.IdType === enums.ID_TYPES.NONE
                        ? 'warning'
                        : 'success'
                    }
                    className="ms-2 fs--2"
                  >
                    {identity.IdType}
                  </SoftBadge>
                </h5>
              </Flex>
            </Flex>
            <hr className="my-0" />
          </Card.Header>

          <Card.Body as={Flex} justifyContent="between" className="pt-1">
            <div className="fs--1">
              <p className="mb-1">
                <span className="fw-semi-bold">Name:</span>{' '}
                {identity.Prefix && identity.Prefix + ' '}
                {identity.FirstName}{' '}
                {identity.MiddleName && identity.MiddleName + ' '}
                {identity.LastName && identity.LastName + ' '} {identity.Suffix}
              </p>
              {identity.IdNumber && (
                <p className="mb-1">
                  <span className="fw-semi-bold">Id Number:</span>{' '}
                  {(identity?.State && identity?.State.length > 0
                    ? `${identity?.State} `
                    : ``) +
                    (identity.IdNumber?.length > 0
                      ? `${identity.IdNumber}`
                      : 'N/A')}
                </p>
              )}
              {identity.IdIssued && (
                <p className="mb-1">
                  <span className="fw-semi-bold">Issue Date:</span>{' '}
                  {identity.IdIssued &&
                    moment(identity.IdIssued).format('MM/DD/YYYY')}
                </p>
              )}
              {identity.IdExpiration && (
                <p className="mb-1">
                  <span className="fw-semi-bold">Expiration Date:</span>{' '}
                  {identity.IdExpiration &&
                    moment(identity.IdExpiration).format('MM/DD/YYYY')}
                </p>
              )}
              {identity.DateOfBirth && (
                <p className="mb-1">
                  <span className="fw-semi-bold">Date of Birth:</span>{' '}
                  {identity.DateOfBirth &&
                    moment(identity.DateOfBirth).format('MM/DD/YYYY')}
                </p>
              )}
              <p className="mb-1">
                <span className="fw-semi-bold">Address:</span>{' '}
                {draftEntryUiHelper.getIdentityAddress({
                  id_address_1: identity.Address1,
                  id_address_2: identity.Address2,
                  id_city: identity.City,
                  id_state: identity.State,
                  id_postal_code: identity.PostalCode
                }) || 'N/A'}
              </p>
              {identity.AdditionalData && (
                <p className="mb-1">
                  <span className="fw-semi-bold">Notes:</span>{' '}
                  <i className="fs--1">{identity.AdditionalData}</i>
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    ) : (
      <></>
    );
  };

  const handleCloseModel = () => {
    setShowDuplicateModal(!showDuplicateModal);
    setSelectedRecordsData([]);
    setParentSigner(null);
    setSelectedIdentifications([]);
    setSelectedDuplicateRecords([]);
    setIsCompleteMargeModel(false);
    setIsFinalConfirmationModel(false);
  };

  return (
    <>
      <Modal
        show={showDuplicateModal}
        onHide={() => handleCloseModel()}
        className="full-screen-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Potential Duplicate Clients</Modal.Title>
        </Modal.Header>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100 m-4">
            <Spinner size="sm" />
            &nbsp;Loading
          </div>
        ) : (
          <>
            {selectedRecordsData[0]?.PotentialDuplicateSigner?.length > 0 ? (
              <>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <div className="my-2">
                    Select a client as a parent record, and then choose which
                    identifications to merge with it. The parent record's name
                    and the selected IDs will carry over after the merge is
                    complete.
                  </div>
                  <div className="my-2">
                    <strong>
                      Important - You can only select one identification of each
                      type across all signers.
                    </strong>
                  </div>
                  <br />
                  <h4>Selected Record</h4>
                  <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                    {selectedRecordsData.map(record => (
                      <li key={record.GUID} style={{ marginBottom: '10px' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <Form.Check
                              type="radio"
                              name="parentSigner"
                              checked={parentSigner === record.GUID}
                              onChange={() => handleRadioChange(record.GUID)}
                              className="mr-2"
                            />
                            <b className="mx-2 px-2">
                              {record.FirstName} {record.LastName}
                              {(record.Identifications.length === 0 ||
                                (record.Identifications.length === 1 &&
                                  (record.Identifications[0].IdType ===
                                    enums.ID_TYPES.NONE ||
                                    record.Identifications[0].IdType ===
                                      enums.ID_TYPES.PERSONALLY_KNOWN))) && (
                                <span className="badge badge-soft-warning rounded-pill">
                                  (Has no associated identifications)
                                </span>
                              )}
                            </b>
                            {parentSigner === record.GUID && (
                              <span className="badge badge-soft-success rounded-pill">
                                Marked As Parent
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Render Identifications */}
                        <Row className="mt-2">
                          {record.Identifications &&
                            _.map(record.Identifications, i => {
                              if (
                                i.IdType !== enums.ID_TYPES.NONE &&
                                i.IdType !== enums.ID_TYPES.PERSONALLY_KNOWN &&
                                !i.IsDeleted
                              ) {
                                return getIdentificationViewCardForMasterSigner(
                                  i,
                                  record
                                );
                              }
                            })}
                          {!record?.Identifications &&
                            record?.Identifications.length == 0 &&
                            'No indetification'}
                        </Row>
                      </li>
                    ))}
                  </ul>
                  <hr />
                  <h4>Matching Records</h4>
                  <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                    {selectedRecordsData[0]?.PotentialDuplicateSigner?.map(
                      record => (
                        <li key={record.GUID} style={{ marginBottom: '10px' }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Form.Check
                                type="radio"
                                name="parentSigner"
                                checked={parentSigner === record.GUID}
                                onChange={() => handleRadioChange(record.GUID)}
                                className="mr-2"
                              />
                              <b className="mx-2 px-2">
                                {record.FirstName} {record.LastName}
                                {(record.Identifications.length === 0 ||
                                  (record.Identifications.length === 1 &&
                                    (record.Identifications[0].IdType ===
                                      enums.ID_TYPES.NONE ||
                                      record.Identifications[0].IdType ===
                                        enums.ID_TYPES.PERSONALLY_KNOWN))) && (
                                  <span className="badge badge-soft-warning rounded-pill">
                                    (Has no associated identifications)
                                  </span>
                                )}
                              </b>
                              {parentSigner === record.GUID && (
                                <span className="badge badge-soft-success rounded-pill">
                                  Marked As Parent
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Render Identifications */}
                          <Row className="mt-2">
                            {record.Identifications &&
                              _.map(record.Identifications, i => {
                                if (
                                  i.IdType !== enums.ID_TYPES.NONE &&
                                  i.IdType !== enums.ID_TYPES.PERSONALLY_KNOWN
                                ) {
                                  return getIdentificationViewCardForMasterSigner(
                                    i,
                                    record
                                  );
                                }
                              })}
                            {!record?.Identifications &&
                              record?.Identifications.length == 0 &&
                              'No indetification'}
                          </Row>
                        </li>
                      )
                    )}
                  </ul>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => handleCloseModel()}
                    disabled={isCompleteMargeModel || isFinalConfirmationModel}
                  >
                    Close
                  </Button>

                  {parentSigner && (
                    <Button
                      variant="primary"
                      onClick={handleCompleteConfirm}
                      disabled={
                        isCompleteMargeModel || isFinalConfirmationModel
                      }
                    >
                      Complete Merge
                    </Button>
                  )}
                </Modal.Footer>
              </>
            ) : (
              <Modal.Body>
                <div className="my-2">No duplicate records found.</div>
              </Modal.Body>
            )}
          </>
        )}
      </Modal>

      {/* confirm  before merge*/}
      <Modal
        show={isCompleteMargeModel}
        onHide={() => setIsCompleteMargeModel(!isCompleteMargeModel)}
        className="full-screen-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Duplicate Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to merge duplicate records with this Master
          Client? This action is permanent and cannot be undone. The
          identification records will be combined, and the original records will
          no longer exist separately.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsCompleteMargeModel(!isCompleteMargeModel)}
          >
            Close
          </Button>

          {parentSigner && (
            <Button
              variant="primary"
              onClick={() => {
                setIsCompleteMargeModel(false);
                setIsFinalConfirmationModel(true);
              }}
            >
              Confirm
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={isFinalConfirmationModel}
        onHide={
          isSubmitLoading
            ? () => {}
            : () => setIsFinalConfirmationModel(!isFinalConfirmationModel)
        }
        className="full-screen-modal"
        centered
        size="lg"
        backdrop="static"
        closable={!isSubmitLoading}
      >
        <Modal.Header closeButton>
          <Modal.Title>Final Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Because this action can not be undone, we require one more
          confirmation. If you wish to proceed with merging these clients,
          please click the confirm button. Otherwise, clicking close will cancel
          this merge
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setIsFinalConfirmationModel(!isFinalConfirmationModel)
            }
            disabled={isSubmitLoading}
          >
            Close
          </Button>

          {parentSigner && (
            <Button variant="primary" onClick={handleSubmit}>
              {isSubmitLoading ? (
                <>
                  <Spinner size="sm" />
                  &nbsp;Loading
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default DuplicateRecord;
